import {AfterViewInit, Directive, ElementRef, Input, NgZone, OnDestroy} from '@angular/core';

/**
 * The design footprint of a fixed-layout block: the CSS-pixel width and height it was laid out
 * for. Below that size the block would clip.
 */
export interface EtsFitToViewportFootprint {
    width: number;
    height: number;
}

/**
 * EtsFitToViewport — scales a fixed-layout block DOWN so that it fits its parent, instead of
 * letting the parent clip it.
 *
 * WHY this exists (2026-09-15): the two-side order dialogs (Hedging Grid transaction popup, APG
 * solution popup) are laid out with fixed pixel columns, ~1.5k px wide for two sides. On a laptop
 * with Windows DPI scaling the CSS viewport is narrower than that, and every section carries
 * `overflow: hidden`, so the put side's GRAND TOTAL and the Copy / Regroup row fell off the
 * screen with nothing to tell the trader. The owner refused scrolling. The alternative is what a
 * user gets from Ctrl+minus, done automatically and only inside the dialog: the block keeps its
 * layout and shrinks uniformly.
 *
 * HOW: after the view is ready (and on every window resize) the directive reads its parent's
 * inner size, computes `f = min(1, parentW / footprint.width, parentH / footprint.height)` and
 * sets the CSS `zoom` property on the host. `zoom` (not `transform: scale`) is used on purpose:
 * it is a LAYOUT property, so the zoomed host still fills 100% of its parent while its content
 * lays out at `parent / f` zoomed pixels, i.e. at least the footprint. Fonts, DevExtreme buttons
 * and flex gaps shrink together. At `f = 1` (any screen big enough) nothing changes.
 *
 * `zoom` is supported by Chrome, Edge and Safari; Firefox added it in 126 (2024). On an older
 * Firefox the property is ignored and the dialog behaves as before.
 *
 * Usage: `<div class="root" [etsFitToViewport]="{width: 1540, height: 640}">`. The parent must be
 * the box that bounds the block (for a dx-popup that is `.dx-popup-content`).
 */
@Directive({
    selector: '[etsFitToViewport]'
})
export class EtsFitToViewportDirective implements AfterViewInit, OnDestroy {

    constructor(
        private readonly _elementRef: ElementRef<HTMLElement>,
        private readonly _ngZone: NgZone
    ) {
    }

    private _footprint: EtsFitToViewportFootprint | null = null;

    private _rafHandle: number | null = null;

    /**
     * The design footprint. `null` / `undefined` disables the directive (zoom reset to 1).
     */
    @Input()
    set etsFitToViewport(value: EtsFitToViewportFootprint | null | undefined) {
        this._footprint = value || null;
        this.scheduleFit();
    }

    /**
     * The scale factor last applied. 1 = the block fits without shrinking.
     */
    get factor(): number {
        return this._factor;
    }

    private _factor = 1;

    private readonly _onResize = () => this.scheduleFit();

    ngAfterViewInit(): void {
        this._ngZone.runOutsideAngular(() => {
            window.addEventListener('resize', this._onResize);
        });
        this.scheduleFit();
    }

    ngOnDestroy(): void {
        window.removeEventListener('resize', this._onResize);
        if (this._rafHandle !== null) {
            cancelAnimationFrame(this._rafHandle);
            this._rafHandle = null;
        }
    }

    /**
     * Coalesces bursts (resize storms, input + view-init) into one measurement per frame. The
     * measurement runs outside Angular: it touches only an inline style, never component state.
     */
    private scheduleFit(): void {
        if (this._rafHandle !== null) {
            return;
        }
        this._ngZone.runOutsideAngular(() => {
            this._rafHandle = requestAnimationFrame(() => {
                this._rafHandle = null;
                this.fit();
            });
        });
    }

    /**
     * The box the block must fit into: the nearest ancestor matching this selector, falling back
     * to the direct parent. Defaults to the DevExtreme popup's content box - the dx template
     * wrapper between it and the host has no size of its own.
     */
    @Input() etsFitToViewportBounds = '.dx-popup-content';

    private fit(): void {
        const host = this._elementRef.nativeElement;
        const parent = (this.etsFitToViewportBounds && host.parentElement
            ? host.parentElement.closest<HTMLElement>(this.etsFitToViewportBounds)
            : null) || host.parentElement;
        const footprint = this._footprint;

        if (!parent || !footprint || !(footprint.width > 0) || !(footprint.height > 0)) {
            this.apply(1);
            return;
        }

        // clientWidth/Height exclude the parent's own border and scrollbar but include its
        // padding; the padding is subtracted so the footprint is compared to the content box.
        const style = getComputedStyle(parent);
        const padX = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
        const padY = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);

        const availW = parent.clientWidth - padX;
        const availH = parent.clientHeight - padY;

        if (availW <= 0 || availH <= 0) {
            // Not laid out yet (e.g. the popup is still opening); keep whatever we have.
            return;
        }

        const f = Math.min(1, availW / footprint.width, availH / footprint.height);

        // Round to 3 decimals: sub-pixel churn on every resize event is noise.
        this.apply(Math.round(f * 1000) / 1000);
    }

    private apply(f: number): void {
        this._factor = f;
        const host = this._elementRef.nativeElement;
        // `zoom` is not in the TS DOM typings for the compiler this project uses.
        (host.style as any).zoom = f < 1 ? String(f) : '';
    }
}
